import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/wam/Nav";
import Grain from "@/components/wam/Grain";
import Cursor from "@/components/wam/Cursor";
import Footer from "@/components/wam/Footer";
import Corners from "@/components/wam/Corners";
import { track } from "@/lib/metaPixel";

export default function ThankYou() {
  const [params] = useSearchParams();
  const firstName = params.get("first_name") || params.get("first") || "";

  // Fire Meta Pixel Lead event exactly once when this page mounts.
  // This is the real conversion event — user actually booked a call.
  useEffect(() => {
    track("Lead", {
      content_name: "WAM Discovery Call Booked",
      content_category: "discovery_call",
      value: 1497,
      currency: "USD",
    });
  }, []);

  return (
    <div className="relative bg-[#070709] text-[#F0ECE4] min-h-screen overflow-x-hidden">
      <Grain />
      <Cursor />
      <Nav />

      {/* Ambient gold radial glow */}
      <div
        aria-hidden
        className="absolute left-1/2 top-[10%] -translate-x-1/2 h-[700px] w-[900px] pointer-events-none opacity-[0.4]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(201,168,76,0.22), transparent 70%)",
        }}
      />

      <main className="relative pt-40 pb-24 md:pt-48 md:pb-32">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10 flex items-center justify-center gap-4"
          >
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#F0ECE4]/40">
              [010] —
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
              CONFIRMED
            </span>
          </motion.div>

          {/* Big gold check */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto mb-10 flex h-[104px] w-[104px] items-center justify-center rounded-full border border-[#C9A84C]/45"
            aria-hidden
          >
            <div className="absolute inset-2 rounded-full bg-[#C9A84C]/10" />
            <svg
              viewBox="0 0 32 32"
              width="42"
              height="42"
              fill="none"
              stroke="#C9A84C"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="relative"
            >
              <motion.path
                d="M7 16 L14 23 L25 10"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              />
            </svg>
            <div className="absolute -inset-1 rounded-full border border-[#C9A84C]/25 animate-ping" style={{ animationDuration: "2.6s" }} />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="font-display mx-auto max-w-[1000px]"
            style={{
              fontSize: "clamp(40px, 6.8vw, 100px)",
              lineHeight: 0.94,
              letterSpacing: "-0.035em",
            }}
            data-testid="thankyou-heading"
          >
            {firstName ? `You're in, ${firstName}.` : "You're in."}
            <br />
            <span className="text-[#C9A84C]">Check your email.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mx-auto mt-8 max-w-[620px] font-body text-[16px] leading-[1.75] text-[#F0ECE4]"
          >
            Your discovery call is locked in. A calendar invite &amp; a short
            pre-call brief just landed in your inbox. Read it before we meet —
            it&rsquo;ll make our 15 minutes ten times more useful.
          </motion.p>

          {/* What happens next — 3 steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mx-auto mt-16 max-w-[880px]"
          >
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#F0ECE4]/40 mb-6">
              WHAT HAPPENS NEXT
            </div>
            <div className="relative border border-[#C9A84C]/25 bg-white/[0.02] p-8 md:p-10 text-left">
              <Corners />
              <ol className="space-y-6">
                {[
                  {
                    n: "01",
                    t: "CHECK YOUR EMAIL",
                    b: "Calendar invite + a short pre-call brief. Add it to your calendar so we don't miss each other.",
                  },
                  {
                    n: "02",
                    t: "COME PREPARED",
                    b: "Think through your niche (VA / FHA / DSCR / etc.) and your current lead sources. We'll dive straight in.",
                  },
                  {
                    n: "03",
                    t: "GET AN HONEST ANSWER",
                    b: "In 15 minutes you'll know exactly whether WAM is the right next move for your pipeline — or not.",
                  },
                ].map((step, i) => (
                  <motion.li
                    key={step.n}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 + i * 0.12 }}
                    className="flex items-start gap-5"
                  >
                    <span className="font-mono text-[11px] tracking-[0.24em] text-[#C9A84C] shrink-0 w-8 pt-1">
                      {step.n}
                    </span>
                    <div>
                      <div className="font-display text-[15px] md:text-[18px] tracking-[-0.02em]">
                        {step.t}
                      </div>
                      <div className="mt-1.5 font-body text-[14px] leading-[1.65] text-[#F0ECE4]/60">
                        {step.b}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>
          </motion.div>

          {/* Secondary actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-14 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/"
              className="wam-btn-ghost text-[12px]"
              data-testid="thankyou-back-btn"
            >
              ← Back to WAM
            </Link>
            <a
              href="mailto:dallas@smallbusinessautomator.com"
              className="wam-btn-ghost text-[12px]"
              data-testid="thankyou-email-btn"
            >
              Email Dallas Directly →
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.45 }}
            transition={{ duration: 0.9, delay: 1.5 }}
            className="mt-10 font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]"
          >
            SEE YOU SOON &middot; DALLAS &amp; THE SBA TEAM
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
